import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Async, Empty, PageHead, Status, useConfirm } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { ApiError, api, type ModelCall } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

export function ModelCallsPage() {
	const t = useT();
	const { locale } = useI18n();
	const confirm = useConfirm();
	const [offset, setOffset] = useState(0);
	const state = useAsync(() => api.modelCalls(offset), [offset]);
	const [selected, setSelected] = useState<ModelCall | null>(null);
	const [detail, setDetail] = useState<unknown>(null);
	const [reference, setReference] = useState("");
	const [note, setNote] = useState("");
	const [decision, setDecision] = useState("receipt");
	const [receipt, setReceipt] = useState("");
	const [approvedCredits, setApprovedCredits] = useState("");
	const approvalValid =
		!approvedCredits.trim() ||
		(/^\d+$/.test(approvedCredits) && Number(approvedCredits) >= 1 && Number(approvedCredits) <= 5000);
	const [busy, setBusy] = useState(false);
	const error = (e: unknown) => toast.error(explain(e));
	// A receipt that is not JSON is the operator's typo, and the parser's
	// sentence says where it is; "not accepted" would send them hunting.
	const explain = (e: unknown) =>
		e instanceof ApiError
			? e.localized(locale)
			: e instanceof SyntaxError
				? t("errors.invalid_json", { detail: e.message })
				: t("errors.bad_request");
	async function inspect(row: ModelCall) {
		setBusy(true);
		try {
			const result = await api.modelCall(row.id);
			setSelected(row);
			setDetail(result);
			setReference("");
			setNote("");
			setReceipt("");
			setApprovedCredits("");
			setDecision("receipt");
		} catch (e) {
			error(e);
		} finally {
			setBusy(false);
		}
	}
	async function submit() {
		if (!selected || (decision === "receipt" && !approvalValid)) return;
		let message: unknown;
		try {
			message = decision === "receipt" ? JSON.parse(receipt) : undefined;
		} catch (e) {
			error(e);
			return;
		}
		const body = {
			request_id: selected.request_id,
			...(decision === "receipt" && approvedCredits.trim() ? { approved_credits: Number(approvedCredits) } : {}),
			reference,
			note,
			decision: decision === "receipt" ? { type: "receipt", message } : { type: "not_billed" },
		};
		setBusy(true);
		try {
			if (
				!(await confirm(
					t("reconcile.apply"),
					t(decision === "receipt" ? "reconcile.chargeConsequence" : "reconcile.releaseConsequence") +
						(decision === "receipt" && approvedCredits.trim()
							? ` ${t("reconcile.approvedAmount", { credits: Number(approvedCredits) })}`
							: ""),
				))
			)
				return;
			await api.reconcileModelCall(selected.id, body);
			setSelected(null);
			state.reload();
		} catch (e) {
			error(e);
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<PageHead title={t("reconcile.title")}>
				<Button variant="default" onClick={state.reload}>
					{t("reconcile.refresh")}
				</Button>
			</PageHead>
			<Async state={state}>
				{({ calls, total }) => (
					<>
						<div className="table-scroll">
							<Table className="text-[length:var(--t-sm)]">
								<TableHeader>
									<TableRow>
										<TableHead>{t("reconcile.call")}</TableHead>
										<TableHead>{t("reconcile.brand")}</TableHead>
										<TableHead>{t("reconcile.status")}</TableHead>
										<TableHead className="num text-right whitespace-nowrap">{t("reconcile.reserved")}</TableHead>
										<TableHead className="text-right">{t("state.actions")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{calls.map((row) => (
										<TableRow key={row.id}>
											<TableCell>
												{row.model}
												<span className="sub mono cell-summary" title={row.request_id}>
													{row.request_id}
												</span>
											</TableCell>
											<TableCell>
												<Link className="cell-summary mono" title={row.org_id} to={`/orgs/${row.org_id}`}>
													{row.org_id}
												</Link>
												<Link className="sub mono cell-summary" title={row.entity_id} to={`/entities/${row.entity_id}`}>
													{row.entity_id}
												</Link>
											</TableCell>
											<TableCell>
												<Status value={row.status} prefix="reconcile" />
											</TableCell>
											<TableCell className="num text-right whitespace-nowrap">{row.reservation}</TableCell>
											<TableCell>
												<Button size="sm" variant="default" disabled={busy} onClick={() => void inspect(row)}>
													{t("reconcile.inspect")}
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						{total === 0 && <Empty body={t("reconcile.empty")} />}
						<div className="table-foot">
							<span>{t("state.of", { shown: Math.min(offset + calls.length, total), total })}</span>
							<span className="spacer" />
							<Button variant="default" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 50))}>
								{t("reconcile.previous")}
							</Button>
							<Button
								variant="default"
								disabled={offset + calls.length >= total}
								onClick={() => setOffset(offset + 50)}
							>
								{t("reconcile.next")}
							</Button>
						</div>
					</>
				)}
			</Async>
			<ChipModal
				open={selected !== null}
				size="lg"
				dismissDisabled={busy}
				srTitle={t("reconcile.inspect")}
				onOpenChange={(open) => {
					if (!open && !busy) setSelected(null);
				}}
			>
				<ChipModalHeader onClose={() => setSelected(null)}>{t("reconcile.inspect")}</ChipModalHeader>
				<ChipModalBody>
					<fieldset className="form" disabled={busy}>
						<ChipModalField
							type="textarea"
							mono
							title={t("reconcile.original")}
							value={JSON.stringify(detail, null, 2)}
							viewOnly
							rows={6}
						/>
						<ChipModalField
							type="input"
							title={t("reconcile.reference")}
							required
							value={reference}
							onChange={setReference}
							maxLength={2000}
							hint={t("reconcile.evidenceRule")}
						/>
						<ChipModalField
							type="textarea"
							title={t("reconcile.note")}
							required
							value={note}
							onChange={setNote}
							maxLength={8000}
							rows={2}
						/>
						<ChipModalField
							type="dropdown"
							title={t("reconcile.decision")}
							value={decision}
							onChange={setDecision}
							options={[
								{ value: "receipt", label: t("reconcile.receipt") },
								{ value: "not_billed", label: t("reconcile.notBilled") },
							]}
							hint={t(decision === "receipt" ? "reconcile.chargeConsequence" : "reconcile.releaseConsequence")}
						/>
						{decision === "receipt" && (
							<ChipModalField
								type="input"
								title={t("reconcile.approvedCredits")}
								value={approvedCredits}
								onChange={setApprovedCredits}
								hint={t("reconcile.approvalRule")}
							/>
						)}
						{decision === "receipt" && (
							<ChipModalField
								type="textarea"
								title={t("reconcile.receiptJson")}
								required
								value={receipt}
								onChange={setReceipt}
								rows={6}
								hint={t("reconcile.receiptShape")}
							/>
						)}
					</fieldset>
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("state.cancel")}
					onCancel={() => setSelected(null)}
					primaryAction={{
						label: t("reconcile.apply"),
						onClick: () => void submit(),
						disabled:
							busy ||
							!reference.trim() ||
							!note.trim() ||
							(decision === "receipt" && (!receipt.trim() || !approvalValid)),
					}}
				/>
			</ChipModal>
		</>
	);
}
